import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertCircle, Calendar, CheckCircle2, CreditCard, Crown, FileText, Hash, Loader2,
    PackageOpen, PlusCircle, Power, RefreshCw, Search, ShieldCheck, UserCheck, UserPlus, Wallet,
} from 'lucide-react';
import styles from './InvoiceHeader.module.css';
import useAccess from '../../../../hooks/useAccess';
import { useStore } from '../../../../context/StoreContext';
import AddCustomerForm from '../../../../components/AddCustomerForm/AddCustomerForm';
import { EXPIRY_WARN_DAYS, LOW_BALANCE_LIMIT, daysLeft, normaliseCards } from '../../Membershippricing';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const API_BASE = `${API_ORIGIN}/api/v1/smiify/sm/customers`;
const ALLOTMENT_PATH = '/card-allotment';
const TONE_ORDER = { danger: 0, warn: 1, ok: 2 };
// what a search result matched on (shown next to the client when it is not the name / mobile)
const MATCH_LABEL = { email: 'Email', family: 'Family', tag: 'Tag', address: 'Address' };

const inr = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '';
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
const last4 = (num) => String(num || '').replace(/\s+/g, '').slice(-4) || '••••';
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}${'•'.repeat(s.length - 4)}${s.slice(-2)}` : s; };
const localYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isMobile = (s) => /^\d{10}$/.test(String(s || ''));
/** "+91 98765 43210" / "098765..." typed or pasted -> "9876543210"; a name / email stays as typed */
const cleanSearch = (raw) => {
    const v = String(raw || '').slice(0, 60);
    if (!/^[\d\s+()-]*$/.test(v)) return v.replace(/^\s+/, '');
    let d = v.replace(/\D/g, '');
    if (d.length > 10 && d.startsWith('91')) d = d.slice(2);
    if (d.length > 10 && d.startsWith('0')) d = d.slice(1);
    return d.slice(0, 10);
};

/* ---------- search helpers ---------- */
const SEARCH_DELAY_MS = 180;          // wait after the last key before asking the server
const CACHE_MS = 60000;               // a search is remembered for a minute
const SERVER_LIMIT = 10;              // the server sends at most this many clients

const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
/** 9811100001 -> 98111 00001 */
const prettyPhone = (p) => { const d = String(p || '').replace(/\D/g, ''); return d.length === 10 ? `${d.slice(0, 5)} ${d.slice(5)}` : d; };
const escapeRx = (v) => String(v).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Marks the typed words inside a text */
const Highlight = ({ text, words, className }) => {
    const t = String(text || '');
    const list = (words || []).filter(Boolean).map(escapeRx);
    if (!list.length || !t) return t;
    const parts = t.split(new RegExp(`(${list.join('|')})`, 'gi'));
    return parts.map((part, i) => (i % 2 === 1 ? <mark key={i} className={className}>{part}</mark> : part));
};
/** Same rule as the server, used to narrow results already on screen while the server answers */
const matchesLocally = (c, q) => {
    const low = q.toLowerCase();
    if (/^\d+$/.test(q)) return String(c.phone || '').includes(q) || (c.family || []).some((f) => String(f.phone || '').includes(q));
    const words = low.split(/\s+/).filter(Boolean);
    return words.every((w) => String(c.name || '').toLowerCase().includes(w))
        || String(c.email || '').toLowerCase().includes(low)
        || (c.family || []).some((f) => String(f.name || '').toLowerCase().includes(low))
        || (c.tags || []).some((t) => String(t).toLowerCase().includes(low))
        || String(c.address || '').toLowerCase().includes(low);
};
const lastVisitText = (d, visits) => {
    const n = Number(visits) || 0;
    if (!d) return n ? `${n} visit${n === 1 ? '' : 's'}` : 'New client';
    const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    const when = days <= 0 ? 'today' : days === 1 ? 'yesterday' : days < 30 ? `${days} days ago` : new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    return `Last visit ${when}${n ? ` · ${n} visit${n === 1 ? '' : 's'}` : ''}`;
};

/** Link that opens the next page with this customer already selected (no need to type the number again) */
const withParams = (base, params) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
};

/** Status + next step for one prepaid card */
const cardState = (card) => {
    const left = daysLeft(card.expiryDate);
    if (card.status === 'EXPIRED' || (left !== null && left <= 0)) return { chip: 'Expired', tone: 'danger', action: 'renew', note: `Expired ${fmtDate(card.expiryDate)}` };
    if (card.balance <= 0) return { chip: 'Empty', tone: 'danger', action: 'recharge', note: `Till ${fmtDate(card.expiryDate)}` };
    if (left !== null && left <= EXPIRY_WARN_DAYS) return { chip: left <= 1 ? 'Today' : `${left}d left`, tone: 'warn', action: 'renew', note: `Till ${fmtDate(card.expiryDate)}` };
    if (card.balance < LOW_BALANCE_LIMIT) return { chip: 'Low', tone: 'warn', action: 'recharge', note: `Till ${fmtDate(card.expiryDate)}` };
    return { chip: 'Active', tone: 'ok', action: null, note: `Till ${fmtDate(card.expiryDate)}` };
};

const CHIP_TONE = { ok: styles.chipOk, warn: styles.chipWarn, danger: styles.chipDanger };

/** Small icon button (label shows on hover and is read by screen readers) */
const IconLink = ({ href, label, icon: Icon, solid = false }) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        title={label}
        className={`${styles.iconBtn} ${solid ? styles.iconBtnSolid : ''}`}
    >
        <Icon size={12} />
    </a>
);

const InvoiceHeader = ({
    customer,
    setCustomer,
    invoiceMeta,
    setInvoiceMeta,
    pricingSettings,
    hasDateChangeAccess,
    wallet,
    appliedMembership = null,
    membershipDeskPath = '/renew-loyalty',
    packages = [],          // client packages: [{ id, name, status, expiryDate, daysLeft, services: [{ key, name, total, left }] }]
    packageUse = {},        // used on this bill: { "packageId|serviceKey": qty }
    onUsePackage = null,    // (pkg, service) => add the service to the bill
}) => {
    const token = localStorage.getItem('smiify_token');
    const { can, canAny, rule, isOwner } = useAccess();
    // Live access (the server checks the same rules)
    const canAddClient = canAny(['customers.create', 'billing.create']);
    const canSeePhone = can('customers.view_phone');
    const canAllot = can('memberships.allot_cards');
    // Recharge / renew a client's card from billing: anyone who can bill, or sell / renew plans
    const canRenew = isOwner || canAny(['billing.create', 'memberships.renew', 'memberships.sell_membership']);
    const backdateDays = isOwner ? 0 : (Number(rule('billing', 'maxBackdateDays', 0)) || 0);
    const todayStr = localYmd(new Date());
    const minDate = backdateDays > 0 ? localYmd(new Date(Date.now() - backdateDays * 86400000)) : undefined;
    const { activeStore } = useStore();
    const searchStoreId = activeStore?._id || activeStore?.id || '';   // a plain id: the search reruns only when the branch really changes

    const safeCustomer = customer || {};
    const safeInvoiceMeta = invoiceMeta || {};

    const [searchQuery, setSearchQuery] = useState(safeCustomer.phone || '');
    const [searchResults, setSearchResults] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [hasSearched, setHasSearched] = useState(false);   // the server answered at least once for this text

    const dropdownRef = useRef(null);   // the search field
    const inputRef = useRef(null);
    const menuRef = useRef(null);       // the dropdown (rendered at the end of the page)
    const cacheRef = useRef(new Map()); // "storeId|text" -> { at, data }
    const abortRef = useRef(null);      // the request in flight
    const latestRef = useRef('');       // the text the screen is showing results for

    const hasStoredCustomerIdentity = isMobile(searchQuery) && String(safeCustomer.name || '').trim().length > 0;
    const isCustomerSelected = hasStoredCustomerIdentity || Boolean(safeCustomer.customerId);
    const canEditInvoiceMeta = Boolean(hasDateChangeAccess);
    const isTextSearch = searchQuery.length > 0 && !/^\d+$/.test(searchQuery);
    const minLength = isTextSearch ? 2 : 3;

    // The box follows the bill's phone only when it changes from outside (another bill opened, client chosen),
    // never because of typing here (typing a name clears the phone, and must not clear the box).
    const ownPhoneRef = useRef(String(safeCustomer.phone || ''));
    useEffect(() => {
        const phone = String(safeCustomer.phone || '');
        if (phone !== ownPhoneRef.current) { ownPhoneRef.current = phone; setSearchQuery(phone); }
    }, [safeCustomer.phone]);
    // another bill: start with its own phone (or an empty box)
    useEffect(() => {
        const phone = String(safeCustomer.phone || '');
        ownPhoneRef.current = phone;
        setSearchQuery(phone);
        setIsDropdownOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeInvoiceMeta.invoiceNo]);

    // closes on a click outside the field and the dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            const inField = dropdownRef.current && dropdownRef.current.contains(event.target);
            const inMenu = menuRef.current && menuRef.current.contains(event.target);
            if (!inField && !inMenu) setIsDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const storeId = searchStoreId;
        const q = searchQuery.trim();
        latestRef.current = q;
        setActiveIndex(-1);
        if (hasStoredCustomerIdentity || !q || q.length < minLength || !storeId || !token) {
            abortRef.current?.abort();
            setSearchResults([]);
            setIsSearching(false);
            setHasSearched(false);
            return undefined;
        }
        const key = `${storeId}|${q.toLowerCase()}`;
        const now = Date.now();

        // 1) same text searched a moment ago: show it at once
        const hit = cacheRef.current.get(key);
        if (hit && now - hit.at < CACHE_MS) {
            setSearchResults(hit.data); setHasSearched(true); setIsSearching(false); setIsDropdownOpen(true);
            return undefined;
        }
        // 2) a shorter text already answered ("kum" -> "kuma"): narrow those results at once
        let prefix = null;
        cacheRef.current.forEach((v, k) => {
            if (now - v.at < CACHE_MS && k.startsWith(`${storeId}|`) && key.startsWith(k) && (!prefix || k.length > prefix.k.length)) prefix = { k, v };
        });
        if (prefix) {
            const local = prefix.v.data.filter((c) => matchesLocally(c, q));
            setSearchResults(local); setHasSearched(true); setIsDropdownOpen(true);
            // that list was complete (fewer than the server limit): nothing new can appear, no need to ask
            if (prefix.v.data.length < SERVER_LIMIT) { cacheRef.current.set(key, { at: now, data: local }); setIsSearching(false); return undefined; }
        }

        // 3) ask the server (the previous request is cancelled)
        const timer = setTimeout(async () => {
            abortRef.current?.abort();
            const ac = new AbortController();
            abortRef.current = ac;
            setIsSearching(true);
            try {
                const response = await fetch(`${API_BASE}/search?query=${encodeURIComponent(q)}`, {
                    headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                    signal: ac.signal,
                });
                const result = await response.json();
                if (!response.ok) return;
                const data = result.data || [];
                cacheRef.current.set(key, { at: Date.now(), data });
                if (cacheRef.current.size > 80) cacheRef.current.delete(cacheRef.current.keys().next().value);
                if (latestRef.current !== q) return;                 // the text changed meanwhile
                setSearchResults(data); setHasSearched(true); setIsDropdownOpen(true);
            } catch (error) {
                if (error.name !== 'AbortError') console.error('Customer search failed:', error);
            } finally {
                if (abortRef.current === ac) setIsSearching(false);
            }
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [searchQuery, searchStoreId, token, hasStoredCustomerIdentity, minLength]);

    // a new or edited client must show up in the next search
    const clearSearchCache = () => cacheRef.current.clear();

    /* ---------- dropdown position: drawn over the page so nothing below can cover or cut it ---------- */
    const showMenu = isDropdownOpen && !isCustomerSelected;
    const placeMenu = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const width = Math.min(Math.max(r.width, 380), window.innerWidth - 16);
        const below = window.innerHeight - r.bottom;
        const above = r.top;
        const openUp = below < 240 && above > below;
        setMenuPos({
            left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
            width,
            ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
            listMax: Math.max(140, Math.min(300, (openUp ? above : below) - 70)),
        });
    }, []);
    useLayoutEffect(() => {
        if (!showMenu) return undefined;
        placeMenu();
        window.addEventListener('resize', placeMenu);
        window.addEventListener('scroll', placeMenu, true);   // any scrolling parent
        return () => { window.removeEventListener('resize', placeMenu); window.removeEventListener('scroll', placeMenu, true); };
    }, [showMenu, placeMenu, searchResults.length]);

    useEffect(() => {
        if (activeIndex < 0 || !menuRef.current) return;
        const el = menuRef.current.querySelector(`[data-index="${activeIndex}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    // ↑ ↓ move, Enter picks, Esc closes
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Escape') { setIsDropdownOpen(false); return; }
        if (!showMenu || !searchResults.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % searchResults.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i <= 0 ? searchResults.length - 1 : i - 1)); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const pick = searchResults[activeIndex >= 0 ? activeIndex : 0];
            if (pick) handleSelectCustomer(pick);
        }
    };

    const handleSearchChange = (event) => {
        const query = cleanSearch(event.target.value);
        setSearchQuery(query);
        const typedNumber = /^\d*$/.test(query);
        const phone = typedNumber ? query : '';
        ownPhoneRef.current = phone;
        const changed = phone !== safeCustomer.phone || !typedNumber;
        setCustomer({
            ...safeCustomer,
            phone,
            customerId: changed ? null : safeCustomer.customerId,
            name: changed ? '' : safeCustomer.name,
            walletBalance: changed ? 0 : safeCustomer.walletBalance,
            dueAmount: changed ? 0 : safeCustomer.dueAmount,
            activePackages: changed ? [] : safeCustomer.activePackages,
            activePrepaidCards: changed ? [] : safeCustomer.activePrepaidCards,
            prepaidMemberships: changed ? [] : safeCustomer.prepaidMemberships,
            discountMemberships: changed ? [] : safeCustomer.discountMemberships,
            _enriched: changed ? false : safeCustomer._enriched,
        });
        setIsDropdownOpen(query.trim().length >= (typedNumber ? 3 : 2));
    };

    const handleSelectCustomer = (selectedCustomer) => {
        setCustomer({
            ...safeCustomer,
            customerId: selectedCustomer._id || selectedCustomer.id,
            phone: selectedCustomer.phone || selectedCustomer.mobile || '',
            name: selectedCustomer.name || selectedCustomer.fullName || '',
            walletBalance: Number(selectedCustomer.walletBalance) || 0,
            dueAmount: Number(selectedCustomer.dueAmount) || Number(selectedCustomer.previousDue) || 0,
            activePackages: selectedCustomer.activePackages || [],
            activePrepaidCards: selectedCustomer.activePrepaidCards || [],
            prepaidMemberships: selectedCustomer.prepaidMemberships || [],
            discountMemberships: selectedCustomer.discountMemberships || [],
            _enriched: true,
        });
        ownPhoneRef.current = String(selectedCustomer.phone || selectedCustomer.mobile || '');
        setSearchQuery(selectedCustomer.phone || selectedCustomer.mobile || '');
        setSearchResults([]);
        setIsDropdownOpen(false);
    };

    const handleCustomerCreated = (newCustomer) => {
        clearSearchCache();
        setIsModalOpen(false);
        handleSelectCustomer(newCustomer);
    };

    const updateInvoiceMeta = (field, value) => {
        if (!canEditInvoiceMeta) return;
        if (field === 'date' && value) {
            if (value > todayStr) return;                       // no future bills
            if (minDate && value < minDate) return;             // above the back-date limit
        }
        setInvoiceMeta({ ...safeInvoiceMeta, [field]: value });
    };

    const isCompleteNumber = isMobile(searchQuery);
    const gstin = String(pricingSettings?.gstin || '').trim().toUpperCase();
    const showGstin = Boolean(pricingSettings?.isGstBill && gstin);

    /* ------------------------------ Memberships ------------------------------ */
    // undefined = loading, false = failed (falls back to search data), object = loaded
    const walletLoading = Boolean(safeCustomer.customerId) && wallet === undefined;

    const cards = useMemo(
        () => normaliseCards(wallet, safeCustomer)
            .map((c) => ({ ...c, state: cardState(c) }))
            .sort((a, b) => TONE_ORDER[a.state.tone] - TONE_ORDER[b.state.tone] || b.balance - a.balance),
        [wallet, safeCustomer]
    );

    const pending = useMemo(
        () => (wallet?.pending || (safeCustomer.prepaidMemberships || []).filter((m) => m.status === 'PENDING_ALLOTMENT')
            .map((m) => ({ id: m._id, name: m.name, purchaseDate: m.purchaseDate }))),
        [wallet, safeCustomer.prepaidMemberships]
    );

    const discounts = useMemo(() => {
        const rank = { ACTIVE: 0, INACTIVE: 1, EXPIRED: 2 };
        return [...(wallet?.discounts || [])]
            .sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || (Number(b.price) || 0) - (Number(a.price) || 0));
    }, [wallet]);

    const who = { phone: safeCustomer.phone, customerId: safeCustomer.customerId };
    const deskUrl = (action, kind, target) => withParams(membershipDeskPath, { ...who, action, kind, target });
    const allotUrl = (extra) => withParams(ALLOTMENT_PATH, { ...who, ...extra });

    const usableBalance = cards.filter((c) => !['Expired', 'Empty'].includes(c.state.chip)).reduce((s, c) => s + c.balance, 0);
    const activeCount = discounts.filter((d) => d.status === 'ACTIVE').length;
    const showKhata = isCustomerSelected && (safeCustomer.dueAmount > 0 || safeCustomer.walletBalance > 0);
    const showMemberships = isCustomerSelected && (walletLoading || cards.length || pending.length || discounts.length || packages.length);
    const activePackageCount = packages.filter((p) => p.status === 'ACTIVE').length;

    /* ------------------------------ Dropdown (portal) ------------------------------ */
    const words = isTextSearch ? searchQuery.trim().split(/\s+/) : [];
    const digitsTyped = !isTextSearch ? searchQuery.trim() : '';
    const firstLoad = isSearching && !hasSearched;
    const dropdown = showMenu && menuPos ? createPortal(
        <div
            ref={menuRef}
            className={styles.dropdownMenu}
            role="listbox"
            id="client-search-list"
            aria-label="Matching clients"
            style={{ position: 'fixed', zIndex: 2000, left: menuPos.left, width: menuPos.width, top: menuPos.top, bottom: menuPos.bottom }}
            onMouseDown={(e) => e.preventDefault()}   // keep the cursor in the search box
        >
            {firstLoad && (
                <div className={styles.dropdownList} aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className={styles.ddSkeleton}><span /><div><span style={{ width: '55%', height: 10 }} /><span style={{ width: '35%', height: 8 }} /></div></div>
                    ))}
                </div>
            )}

            {!firstLoad && searchResults.length > 0 && (
                <div className={styles.dropdownList} style={{ maxHeight: menuPos.listMax }}>
                    {searchResults.map((cust, i) => {
                        const tag = MATCH_LABEL[cust.matchedOn];
                        const phone = cust.phone || cust.mobile;
                        const due = Number(cust.dueAmount) || 0;
                        const adv = Number(cust.walletBalance) || 0;
                        const active = i === activeIndex;
                        return (
                            <button
                                type="button"
                                role="option"
                                aria-selected={active}
                                data-index={i}
                                key={cust._id || phone}
                                className={`${styles.dropdownItem} ${active ? styles.dropdownItemActive : ''}`}
                                onMouseEnter={() => setActiveIndex(i)}
                                onClick={() => handleSelectCustomer(cust)}
                            >
                                <span className={styles.custAvatar} aria-hidden="true">{initials(cust.name || cust.fullName)}</span>
                                <span className={styles.custDetails}>
                                    <span className={styles.custName}><Highlight text={cust.name || cust.fullName} words={words} className={styles.hit} /></span>
                                    <span className={styles.custLine}>
                                        <span className={styles.custPhone}>
                                            {canSeePhone ? <Highlight text={prettyPhone(phone)} words={digitsTyped ? [digitsTyped.length > 5 ? prettyPhone(digitsTyped).trim() : digitsTyped] : []} className={styles.hit} /> : maskPhone(phone)}
                                        </span>
                                        <span className={styles.custDot} aria-hidden="true" />
                                        <span className={styles.custSub}>
                                            {cust.matchedOn === 'email' && cust.email ? cust.email : lastVisitText(cust.lastVisitDate, cust.totalVisits)}
                                        </span>
                                    </span>
                                </span>
                                <span className={styles.custRight}>
                                    {due > 0 && <span className={styles.dueTag}>Due {inr(due)}</span>}
                                    {adv > 0 && <span className={styles.advTag}>Adv {inr(adv)}</span>}
                                    {tag && <span className={styles.matchTag}>{tag}</span>}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {!firstLoad && !isSearching && hasSearched && searchResults.length === 0 && searchQuery.trim().length >= minLength && (
                <div className={styles.noResultText}>
                    <strong>No client found</strong>
                    <span>{isTextSearch ? `Nobody matches "${searchQuery.trim()}" in this branch.` : 'No client with this mobile number yet.'}</span>
                </div>
            )}

            {canAddClient && (
                <button
                    type="button"
                    className={`${styles.addNewBtn} ${hasSearched && !searchResults.length && !isSearching ? styles.addNewBtnStrong : ''}`}
                    onClick={() => { setIsDropdownOpen(false); setIsModalOpen(true); }}
                >
                    <UserPlus size={13} /> Add new client{/^\d{10}$/.test(searchQuery) ? ` (${prettyPhone(searchQuery)})` : ''}
                </button>
            )}
        </div>,
        document.body,
    ) : null;

    return (
        <div className={styles.headerWrapper}>
            {isModalOpen && (
                <AddCustomerForm onClose={() => setIsModalOpen(false)} onSuccess={handleCustomerCreated} initialPhone={/^\d+$/.test(searchQuery) ? searchQuery : ''} />
            )}
            {dropdown}

            <section className={styles.customerPanel} aria-label="Customer">
                <div className={styles.lookupFields}>
                    <div className={styles.phoneLookup} ref={dropdownRef}>
                        <label className={`${styles.inputGroup} ${styles.phoneField}`}>
                            <span>Search client</span>
                            <div className={styles.inputShell}>
                                {isSearching ? <Loader2 size={13} className={`${styles.fieldIcon} ${styles.spin}`} /> : <Search size={13} className={styles.fieldIcon} />}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    inputMode="search"
                                    maxLength={60}
                                    placeholder="Mobile, name or email"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onFocus={() => (!isCustomerSelected && searchQuery.trim().length >= minLength && setIsDropdownOpen(true))}
                                    onKeyDown={handleSearchKeyDown}
                                    className={styles.cleanInput}
                                    autoComplete="off"
                                    role="combobox"
                                    aria-expanded={showMenu}
                                    aria-controls="client-search-list"
                                    aria-autocomplete="list"
                                />
                                {isCustomerSelected && <CheckCircle2 size={14} className={styles.inputSuccessIcon} />}
                            </div>
                        </label>
                    </div>

                    <label className={`${styles.inputGroup} ${styles.nameField}`}>
                        <span>Customer name</span>
                        <div className={styles.inputShell}>
                            <UserCheck size={13} className={styles.fieldIcon} />
                            <input
                                type="text"
                                placeholder="Customer name"
                                value={safeCustomer.name || ''}
                                onChange={(event) => setCustomer({ ...safeCustomer, name: event.target.value })}
                                className={styles.cleanInput}
                                autoComplete="off"
                            />
                        </div>
                    </label>
                </div>

                {!isCustomerSelected && searchQuery.length > 0 && !(isTextSearch && showMenu) && (
                    <div className={styles.lookupEmpty} data-state={isCompleteNumber ? 'not-found' : 'incomplete'}>
                        <span className={styles.lookupEmptyIcon}><AlertCircle size={15} /></span>
                        <div>
                            {isTextSearch ? (
                                <>
                                    <strong>Choose the client from the list</strong>
                                    <span>Searching by name, email, family or tag. For a walk-in, type the 10-digit mobile.</span>
                                </>
                            ) : (
                                <>
                                    <strong>{isCompleteNumber ? 'Customer not found' : 'Keep entering the mobile number'}</strong>
                                    <span>{isCompleteNumber ? (canAddClient ? 'Create a new client or continue as walk-in.' : 'Continue as walk-in.') : `${10 - searchQuery.length} digits remaining`}</span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {(showKhata || showMemberships) && (
                    <div className={styles.mWrap} style={{ maxHeight: 230, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                        {/* ---------------- Khata ---------------- */}
                        {showKhata && (
                            <div className={styles.mBox}>
                                <div className={styles.mHead}><span><Wallet size={11} /> Khata</span></div>
                                <div className={styles.mList}>
                                    {safeCustomer.dueAmount > 0 && (
                                        <div className={styles.mRow}>
                                            <span className={styles.mMain}><strong>Due</strong></span>
                                            <span className={`${styles.mAmount} ${styles.mAmountDue}`}>{inr(safeCustomer.dueAmount)}</span>
                                        </div>
                                    )}
                                    {safeCustomer.walletBalance > 0 && (
                                        <div className={styles.mRow}>
                                            <span className={styles.mMain}><strong>Advance</strong></span>
                                            <span className={`${styles.mAmount} ${styles.mAmountAdvance}`}>{inr(safeCustomer.walletBalance)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {walletLoading && (
                            <div className={styles.mBox}>
                                <div className={styles.mLoading}><Loader2 size={12} className={styles.spin} /> Loading memberships...</div>
                            </div>
                        )}

                        {/* ---------------- Discount memberships ---------------- */}
                        {!walletLoading && discounts.length > 0 && (
                            <div className={styles.mBox}>
                                <div className={styles.mHead}>
                                    <span><Crown size={11} /> Discount</span>
                                    <small>{activeCount} active</small>
                                </div>
                                <div className={styles.mList}>
                                    {discounts.map((d) => {
                                        const left = daysLeft(d.expiryDate);
                                        const applied = appliedMembership?.id === d.id;
                                        const expiringSoon = d.status === 'ACTIVE' && left !== null && left <= EXPIRY_WARN_DAYS;
                                        return (
                                            <div className={styles.mRow} key={d.id}>
                                                <span className={styles.mMain}>
                                                    <strong>{d.name}</strong>
                                                    <small>
                                                        {d.status === 'ACTIVE' && `${d.serviceDiscount}% off, till ${fmtDate(d.expiryDate)}`}
                                                        {d.status === 'INACTIVE' && 'Not activated'}
                                                        {d.status === 'EXPIRED' && `Expired ${fmtDate(d.expiryDate)}`}
                                                    </small>
                                                </span>
                                                <span className={styles.mRight}>
                                                    {d.status === 'ACTIVE' && applied && <span className={`${styles.mChip} ${styles.chipApplied}`}><CheckCircle2 size={9} /> On bill</span>}
                                                    {expiringSoon && <span className={`${styles.mChip} ${styles.chipWarn}`}>{left <= 1 ? 'Today' : `${left}d left`}</span>}
                                                    {d.status === 'INACTIVE' && canAllot && <IconLink href={allotUrl({ activate: d.id })} label="Activate membership" icon={Power} solid />}
                                                    {(d.status === 'EXPIRED' || expiringSoon) && canRenew && <IconLink href={deskUrl('RENEW', 'DISCOUNT', d.id)} label="Renew membership" icon={RefreshCw} />}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ---------------- Prepaid cards ---------------- */}
                        {!walletLoading && (cards.length > 0 || pending.length > 0) && (
                            <div className={styles.mBox}>
                                <div className={styles.mHead}>
                                    <span><CreditCard size={11} /> Prepaid cards</span>
                                    {cards.length > 0 && <small>{inr(usableBalance)} usable</small>}
                                </div>
                                <div className={styles.mList}>
                                    {pending.map((p) => (
                                        <div className={styles.mRow} key={`p-${p.id}`}>
                                            <span className={styles.mMain}>
                                                <strong>{p.name}</strong>
                                                <small>Card not allotted</small>
                                            </span>
                                            <span className={styles.mRight}>
                                                <span className={`${styles.mChip} ${styles.chipWarn}`}>Waiting</span>
                                                {canAllot && <IconLink href={allotUrl({ allot: p.id })} label="Allot card" icon={CreditCard} solid />}
                                            </span>
                                        </div>
                                    ))}
                                    {cards.map((card) => (
                                        <div className={styles.mRow} key={card.id}>
                                            <span className={styles.mMain}>
                                                <strong>{card.name} <em>•{last4(card.number)}</em></strong>
                                                <small>{card.state.note}</small>
                                            </span>
                                            <span className={styles.mRight}>
                                                <span className={styles.mAmount}>{inr(card.balance)}</span>
                                                {card.state.tone !== 'ok' && (
                                                    <span className={`${styles.mChip} ${CHIP_TONE[card.state.tone]}`}>{card.state.chip}</span>
                                                )}
                                                {/* expired: renew. expiring soon: renew or recharge. any live card: recharge (strong when low / empty) */}
                                                {canRenew && card.state.action === 'renew' && <IconLink href={deskUrl('RENEW', 'PREPAID', card.id)} label="Renew card" icon={RefreshCw} />}
                                                {canRenew && card.state.chip !== 'Expired' && (
                                                    <IconLink
                                                        href={deskUrl(card.state.chip === 'Empty' && card.status !== 'ASSIGNED' ? 'RENEW' : 'RECHARGE', 'PREPAID', card.id)}
                                                        label={card.state.action === 'recharge' ? `Recharge card (balance ${inr(card.balance)})` : 'Add balance to this card'}
                                                        icon={PlusCircle}
                                                        solid={card.state.action === 'recharge'}
                                                    />
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ---------------- Packages ---------------- */}
                        {!walletLoading && packages.length > 0 && (
                            <div className={styles.mBox}>
                                <div className={styles.mHead}>
                                    <span><PackageOpen size={11} /> Packages</span>
                                    <small>{activePackageCount} active</small>
                                </div>
                                <div className={styles.mList}>
                                    {packages.map((pkg) => {
                                        const live = pkg.status === 'ACTIVE';
                                        const soon = live && pkg.daysLeft !== null && pkg.daysLeft <= EXPIRY_WARN_DAYS;
                                        return (
                                            <div key={pkg.id} style={{ padding: '4px 0', opacity: live ? 1 : 0.55 }}>
                                                <div className={styles.mRow}>
                                                    <span className={styles.mMain}>
                                                        <strong>{pkg.name}</strong>
                                                        <small>
                                                            {live ? `${pkg.totalLeft} of ${pkg.totalQty} left, till ${fmtDate(pkg.expiryDate)}`
                                                                : pkg.status === 'EXPIRED' ? `Expired ${fmtDate(pkg.expiryDate)}` : 'All services used'}
                                                        </small>
                                                    </span>
                                                    <span className={styles.mRight}>
                                                        {soon && <span className={`${styles.mChip} ${styles.chipWarn}`}>{pkg.daysLeft <= 1 ? 'Today' : `${pkg.daysLeft}d left`}</span>}
                                                        {!live && <span className={`${styles.mChip} ${styles.chipDanger}`}>{pkg.status === 'EXPIRED' ? 'Expired' : 'Used up'}</span>}
                                                    </span>
                                                </div>
                                                {live && pkg.services.map((svc) => {
                                                    const inBill = packageUse[`${pkg.id}|${svc.key}`] || 0;
                                                    const left = Math.max(0, svc.left - inBill);
                                                    return (
                                                        <div key={svc.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0 3px 10px', fontSize: 11 }}>
                                                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 }}>
                                                                {svc.name}
                                                            </span>
                                                            <span style={{ fontWeight: 800, color: left > 0 ? 'var(--h-color-success-600, #007a49)' : 'var(--h-color-neutral-400, #9d9ea4)' }}>
                                                                {left}/{svc.total}
                                                            </span>
                                                            {inBill > 0 && <span className={`${styles.mChip} ${styles.chipApplied}`}>{inBill} on bill</span>}
                                                            {onUsePackage && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onUsePackage(pkg, svc)}
                                                                    disabled={left <= 0}
                                                                    title={left > 0 ? `Add ${svc.name} to this bill (free, already paid in the package)` : 'Nothing left'}
                                                                    style={{
                                                                        height: 20, padding: '0 8px', border: 'none', borderRadius: 6, fontFamily: 'inherit', fontSize: 10.5, fontWeight: 800,
                                                                        background: left > 0 ? 'var(--h-color-primary-600, #673de6)' : 'var(--h-color-neutral-150, #e9e9ec)',
                                                                        color: left > 0 ? '#fff' : 'var(--h-color-neutral-500, #797980)', cursor: left > 0 ? 'pointer' : 'not-allowed',
                                                                    }}
                                                                >
                                                                    Use
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </section>

            <section className={styles.invoicePanel} aria-label="Invoice details">
                <div className={styles.invoicePanelHeader}>
                    <div><span className={styles.eyebrow}>Document control</span><h2 className={styles.panelTitle}>Invoice details</h2></div>
                    <div className={styles.invoiceIcon}><FileText size={15} /></div>
                </div>
                <div className={styles.metaList}>
                    <div className={styles.metaRow}>
                        <span className={styles.metaLabel}><Hash size={12} /> Invoice number</span>
                        <input type="text" value={safeInvoiceMeta.invoiceNo || ''} readOnly className={`${styles.metaInput} ${styles.highlightText}`} aria-label="Invoice number" />
                    </div>
                    <div className={styles.metaRow}>
                        <label className={styles.metaLabel} htmlFor="invoice-date"><Calendar size={12} /> Invoice date</label>
                        <input id="invoice-date" type="date" value={safeInvoiceMeta.date || ''} min={minDate} max={todayStr} onChange={(event) => updateInvoiceMeta('date', event.target.value)} disabled={!canEditInvoiceMeta} className={styles.metaInput} title={canEditInvoiceMeta ? (backdateDays ? `Up to ${backdateDays} day(s) back` : '') : 'You cannot change the bill date'} />
                    </div>
                    {showGstin && (
                        <div className={styles.metaRow}>
                            <span className={styles.metaLabel}><ShieldCheck size={12} /> GSTIN</span>
                            <span className={styles.metaValue}>{gstin}</span>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default InvoiceHeader;